'use client';

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import { DEVICE_WIDTHS } from '@/types/feedback';

import { useViewportAutoScale } from './useViewportAutoScale';
import { VIEWPORT_TRANSITION_MS } from './viewportConstants';

import type { DevicePreset } from '@/types/feedback';

interface ViewportContainerProps {
  device: DevicePreset;
  fullscreen?: boolean;
  zoomMultiplier?: number;
  onEffectiveScaleChange?: (scale: number) => void;
  children: ReactNode;
}

export function ViewportContainer({
  device,
  fullscreen = false,
  zoomMultiplier = 1,
  onEffectiveScaleChange,
  children,
}: ViewportContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const deviceWidth = DEVICE_WIDTHS[device];

  const autoScale = useViewportAutoScale(containerRef, deviceWidth);
  const scale = autoScale * zoomMultiplier;

  useEffect(() => {
    onEffectiveScaleChange?.(scale);
  }, [scale, onEffectiveScaleChange]);

  const scaledWidth = deviceWidth * scale;

  const TRANSITION = `width ${VIEWPORT_TRANSITION_MS}ms ease, height ${VIEWPORT_TRANSITION_MS}ms ease, margin ${VIEWPORT_TRANSITION_MS}ms ease, border-radius ${VIEWPORT_TRANSITION_MS}ms ease, box-shadow ${VIEWPORT_TRANSITION_MS}ms ease, transform ${VIEWPORT_TRANSITION_MS}ms ease, padding ${VIEWPORT_TRANSITION_MS}ms ease`;

  const frameStyle: CSSProperties = fullscreen
    ? {
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 0,
        marginBottom: 0,
        overflow: 'hidden',
        borderRadius: 0,
        boxShadow: '0 0 0 0 rgba(0,0,0,0)',
        transition: TRANSITION,
      }
    : {
        width: scaledWidth,
        height: 'calc(100% - 40px)',
        backgroundColor: '#fff',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 20,
        marginBottom: 20,
        overflow: 'hidden',
        borderRadius: 12,
        boxShadow:
          '0 4px 6px -1px rgba(0,0,0,0.08), 0 12px 32px -4px rgba(0,0,0,0.12)',
        transition: TRANSITION,
      };

  const innerStyle: CSSProperties = fullscreen
    ? {
        width: '100%',
        height: '100%',
        transformOrigin: 'top left',
        transform: 'scale(1)',
        transition: TRANSITION,
      }
    : {
        width: deviceWidth,
        height: `${100 / scale}%`,
        transformOrigin: 'top left',
        transform: `scale(${scale})`,
        transition: TRANSITION,
      };

  const overflowsContainer = !fullscreen && zoomMultiplier > 1;

  return (
    <div
      ref={containerRef}
      className={`flex-1 relative ${overflowsContainer ? 'overflow-auto' : 'overflow-hidden'}`}
      style={{
        minWidth: 0,
        paddingLeft: fullscreen ? 0 : 20,
        paddingRight: fullscreen ? 0 : 20,
        transition: TRANSITION,
      }}
    >
      <div style={frameStyle}>
        <div style={innerStyle}>{children}</div>
      </div>
    </div>
  );
}
